import { ChatWithRequestPane } from '@strata/react-chat';
import { SuccessToast } from '@strata/react-toast-message';
import { type ComponentProps } from 'react';
import { useParams } from 'react-router';

import { ChatDetails } from 'components/requests/ChatDetails';
import { type RequestDetails } from 'services/interfaces';
import { useUserNameMap } from 'store/api/authApi';
import {
    useDeleteRequestNoteMutation,
    useGetNotesForRequestQuery,
    usePostRequestNoteMutation,
} from 'store/api/payerRequestApi';

interface NotesTabProps {
    canWriteRequestType: boolean;
    isLoading: boolean;
    requestDetails?: RequestDetails;
}

export const NotesTab = ({
    canWriteRequestType,
    isLoading,
    requestDetails = undefined,
}: NotesTabProps) => {
    const { payerId, requestId } = useParams() as { payerId: string; requestId: string };

    const userNameMap = useUserNameMap(payerId);

    const [postRequestNote, { isLoading: postRequestNoteLoading }] = usePostRequestNoteMutation();
    const [deleteRequestNote, { isLoading: deleteRequestNoteLoading }] =
        useDeleteRequestNoteMutation();

    const { data, isFetching } = useGetNotesForRequestQuery({ payerId, requestId });
    const notes = data?.map((note) => ({
        ...note,
        senderFullName: userNameMap?.[note.upn],
    }));

    const onNoteDelete: ComponentProps<typeof ChatDetails>['onMessageDelete'] = async ({
        messageId,
    }) => {
        await deleteRequestNote({
            payerId,
            requestId,
            requestNoteId: messageId,
        }).unwrap();
        SuccessToast({ message: 'Note deleted successfully.' });
    };

    return (
        <ChatDetails
            notesForRequest={notes}
            requestDetails={requestDetails}
            canWriteRequestType={canWriteRequestType}
            conversationType={ChatWithRequestPane.ConversationType.NOTES}
            isLoading={
                isLoading || isFetching || postRequestNoteLoading || deleteRequestNoteLoading
            }
            onMessageDelete={canWriteRequestType ? onNoteDelete : undefined}
            onMessageSend={async ({ details }) => {
                await postRequestNote({
                    payerId,
                    requestId,
                    requestNote: details.message,
                }).unwrap();
                SuccessToast({ message: 'Note saved successfully.' });
            }}
        />
    );
};
